/**
 * 금액 한글 읽기 — 금액 칸에 '15만 8천원' 말풍선을 띄운다 (사이트 공용)
 *
 * 숫자만 보면 0 하나를 더 치거나 빼도 눈에 안 띈다. 만 단위 읽기가 바로 옆에 뜨면
 * '69만원' 과 '6만 9천원' 은 한눈에 갈린다 — 돈을 움직이는 칸의 오타 방지용이다.
 *
 * 선언형 — input 에 속성만 달면 된다 (문서 위임이라 동적 삽입·모달 안 OK):
 *   <input name="amountof" data-amount-kor>            → '6만 9천원'
 *   <input name="price"    data-amount-kor="캐시">      → '6만 9천캐시' (값 = 뒤에 붙일 단위, 비우면 '원')
 *
 * 칸에 **포커스가 있는 동안만** 뜬다. 값을 JS 로 채운 직후 보여줄 필요가 있으면
 *   $j.Ui.AmountKor.refresh(inputEl);
 *
 * 모양은 '누를 수 있는 칩'이 아니라 **도움말 말풍선**이다 — 반전 배경·꼬리, 테두리·그림자 없음,
 * 포인트색·링크색 금지. 옆의 [전액] 같은 버튼과 같은 모양이면 눌러야 하는 것처럼 보인다.
 *
 * 말풍선은 body 에 하나만 두고 칸에 붙인다(position:fixed). 칸을 감싸는 래퍼를
 * 만들지 않는 것은 일부러다 — input-group·표 셀·flex 줄에 끼워진 칸의 레이아웃을
 * 건드리지 않고, overflow:hidden 인 모달·표 안에서도 잘리지 않는다.
 * 칸 **아래·오른쪽 끝**에 붙이고, 아래 공간(키보드·스크롤 영역 하단)이 모자라면 위로 뒤집는다.
 *
 * 색·치수는 `--ui-amount-kor-*` 만 읽는다. 사이트 연결은 사이트 scss 가 한다
 * (admin·mobile 의 components/_amount-kor.scss, madmin/main.scss). CLAUDE.md 「공용 UI 컴포넌트는 사이트를 모른다」.
 * 값 → 읽기 변환 자체는 `$j.Common.Util.toKoreanAmount()` 다.
 */
import Util from '../Common/Util';

const SEL = 'input[data-amount-kor]';
const TIP_ID = 'ui-amount-kor';

export default class AmountKor {
    constructor() {
        this.$el = null;
        this.anchor = null;
        this._bind();
    }

    /* ───────────────── public ───────────────── */

    /** 칸의 현재 값으로 다시 그린다. 포커스가 없는 칸이면 아무것도 하지 않는다. */
    refresh = (input) => {
        const el = input instanceof $ ? input[0] : input;
        if (!el || el !== document.activeElement) return;
        this._show(el);
    };

    hide = () => {
        if (this.anchor) this._unlink(this.anchor);
        this.anchor = null;
        if (this.$el) this.$el.removeClass('is-open');
    };

    /* ───────────────── internal ───────────────── */

    _bind() {
        // 칸 자체의 콤마 핸들러(formatNumberInput)가 먼저 돌고 document 까지 올라온 뒤에 읽는다
        $(document)
            .on('focus.amountKor input.amountKor', SEL, (e) => this._show(e.currentTarget))
            .on('blur.amountKor', SEL, () => this.hide());

        // fixed 라 스크롤·리사이즈(키보드 포함)에 칸과 어긋난다 — 붙어 있는 동안만 따라간다
        const follow = () => { if (this.anchor) this._place(this.anchor); };
        window.addEventListener('scroll', follow, true);
        window.addEventListener('resize', follow);
        if (window.visualViewport) window.visualViewport.addEventListener('resize', follow);
    }

    _show(el) {
        const text = Util.toKoreanAmount(el.value);
        if (!text) { this.hide(); return; }

        if (this.anchor && this.anchor !== el) this._unlink(this.anchor);
        const unit = el.getAttribute('data-amount-kor') || '원';
        this._getEl().text(text + unit).addClass('is-open');
        this.anchor = el;
        this._link(el);
        this._place(el);
    }

    /** 스크린리더가 칸과 말풍선을 잇게 — 칸이 원래 가진 describedby 는 보존한다 */
    _link(el) {
        const ids = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
        if (!ids.includes(TIP_ID)) el.setAttribute('aria-describedby', ids.concat(TIP_ID).join(' '));
    }

    _unlink(el) {
        const ids = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== TIP_ID);
        if (ids.length) el.setAttribute('aria-describedby', ids.join(' '));
        else el.removeAttribute('aria-describedby');
    }

    /**
     * ⚠ 좌표계가 둘이다 — 글자크기를 `body { zoom }` 으로 키우는 사이트(adm·m)에선 말풍선도 확대된다.
     *   getBoundingClientRect()·뷰포트 → 확대된 화면 px / outerWidth()·넣는 top/left → 확대 전 px.
     *   계산은 화면 px 로 하고 넣을 때만 zoom 으로 나눈다 (Ui/Tooltip.js _place 와 같은 규칙).
     */
    _place(el) {
        const r = el.getBoundingClientRect();
        const floor = this._floor(el);
        // 칸이 화면(또는 스크롤 영역) 밖으로 밀려나면 떠 있는 말풍선만 남지 않게 숨긴다
        if (r.bottom < 0 || r.top > floor || (!r.width && !r.height)) {
            this.$el.removeClass('is-open');
            return;
        }
        this.$el.addClass('is-open');

        const z = this._zoom();
        const cs = getComputedStyle(this.$el[0]);
        const gap = (parseFloat(cs.getPropertyValue('--ui-amount-kor-gap')) || 6) * z;
        const inset = (parseFloat(cs.getPropertyValue('--ui-amount-kor-inset')) || 8) * z;
        const w = this.$el.outerWidth() * z;
        const h = this.$el.outerHeight() * z;

        const up = floor - r.bottom < h + gap + 12 && r.top - gap - h > 0;
        const top = up ? r.top - gap - h : r.bottom + gap;
        let left = r.right - inset - w;
        left = Math.max(4, Math.min(left, window.innerWidth - w - 4));

        this.$el.toggleClass('is-up', up)
            .css({ top: Math.round(top / z), left: Math.round(left / z), zIndex: this._zAbove(el) });
    }

    /**
     * 말풍선이 내려갈 수 있는 바닥(화면 px). 보이는 화면 하단(키보드 제외)과
     * 칸을 품은 스크롤 영역(모달 본문·표 래퍼) 하단 중 높은 쪽.
     */
    _floor(el) {
        const vv = window.visualViewport;
        let floor = vv ? vv.offsetTop + vv.height : window.innerHeight;
        for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
            // 스크롤 영역만 본다 — clearfix 용 overflow:hidden 래퍼가 칸에 딱 붙어 있으면 늘 뒤집혀 버린다
            const oy = getComputedStyle(n).overflowY;
            if (oy !== 'auto' && oy !== 'scroll') continue;
            floor = Math.min(floor, n.getBoundingClientRect().bottom);
        }
        return floor;
    }

    /** 말풍선이 놓인 좌표계의 배율 — body 에 붙으므로 body 의 zoom 이 곧 우리 배율이다 */
    _zoom() {
        const z = parseFloat(getComputedStyle(document.body).zoom);
        return z > 0 ? z : 1;
    }

    /**
     * 칸을 품은 레이어보다 한 칸 위. 모달은 겹칠수록 z 가 올라가서(Ui/Layer 가 20씩)
     * 고정 슬롯 하나로는 두 번째 모달 안의 칸에서 말풍선이 모달 뒤로 깔린다.
     */
    _zAbove(el) {
        let top = 0;
        for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
            const z = parseInt(getComputedStyle(n).zIndex, 10);
            if (!isNaN(z) && z > top) top = z;
        }
        // 기본 슬롯(popover)보다 낮아질 때는 비워서 스타일시트 값을 쓴다 — 고정헤더(1030) 밑으로 가지 않게
        return top >= 1065 ? top + 1 : '';
    }

    _getEl() {
        if (!this.$el) {
            this._injectStyle();
            this.$el = $(`<div class="ui-amount-kor" id="${TIP_ID}" aria-live="polite"></div>`).appendTo('body');
        }
        return this.$el;
    }

    /**
     * 기본값(:root 한 벌) = 관리자 라이트 톤. 사이트가 자기 scss 에서 덮는다.
     * gap·inset 은 JS 가 읽어 좌표를 잡는 값이라 단위 없는 px 숫자로 둔다.
     */
    _injectStyle() {
        const css = `
:root{--ui-amount-kor-bg:#1E2A3B;--ui-amount-kor-fg:#fff;--ui-amount-kor-radius:4px;
  --ui-amount-kor-font:12px;--ui-amount-kor-pad:4px 8px;--ui-amount-kor-tail-x:14px;
  --ui-amount-kor-gap:6;--ui-amount-kor-inset:6}
.ui-amount-kor{position:fixed;top:0;left:0;z-index:var(--z-index-popover,1065);display:none;
  padding:var(--ui-amount-kor-pad);white-space:nowrap;pointer-events:none;
  font-size:var(--ui-amount-kor-font);font-weight:500;letter-spacing:-.3px;line-height:1.4;
  background:var(--ui-amount-kor-bg);color:var(--ui-amount-kor-fg);border-radius:var(--ui-amount-kor-radius)}
.ui-amount-kor.is-open{display:block}
.ui-amount-kor::before{content:"";position:absolute;right:var(--ui-amount-kor-tail-x);top:-5px;
  border:5px solid transparent;border-top:0;border-bottom-color:var(--ui-amount-kor-bg)}
.ui-amount-kor.is-up::before{top:auto;bottom:-5px;border-bottom:0;border-top:5px solid var(--ui-amount-kor-bg)}
`;
        $('<style>').text(css).appendTo('head');
    }
}
